import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import { fmtMoney, fmtPct } from '../../../core/utils/format';
import { usePlaceBid } from '../contexts/event';
import { fx } from '../utils/fx';

// Fase live-B — panel de puja TAP-TAP. Derecha suma, izquierda resta (por si se
// equivoca). Verde al subir, rojo al bajar; sonido + vibración; mensajes de
// motivación por hito. El tap-tap arma un % LOCAL; "Confirmar" envía UNA puja
// (ajusta el share_bid del participante). Cap = % libre de la vaca.

const MILESTONES = [
    { at: 30, msg: '¡Máximo poder!' },
    { at: 20, msg: '¡Impresionante!' },
    { at: 15, msg: '¡Vas con todo!' },
    { at: 10, msg: '¡Todo un inversionista!' },
    { at: 5,  msg: '¡Buen comienzo!' },
    { at: 1,  msg: '¡Empezaste!' },
];
const messageFor = (pct) => MILESTONES.find((m) => pct >= m.at)?.msg || 'Toca la derecha para sumar tu %';

export default function BidPanel({ token, participant, focal }) {
    const bid = usePlaceBid(token, participant?.participant_token);
    const cap = Math.max(0, Math.floor(focal?.free ?? 30));
    const price = focal?.price ?? null;

    const [target, setTarget] = useState(cap > 0 ? 1 : 0);
    const [confirmed, setConfirmed] = useState(0);
    const [flash, setFlash] = useState(null);
    const flashT = useRef(null);
    useEffect(() => () => clearTimeout(flashT.current), []);

    const bump = (dir) => {
        const next = Math.min(cap, Math.max(0, target + dir));
        if (next === target) return; // en el tope: no-op
        const crossedUp = dir > 0 && MILESTONES.some((m) => target < m.at && next >= m.at);
        if (crossedUp) fx.milestone();
        else if (dir > 0) fx.up();
        else fx.down();
        setTarget(next);
        setFlash(dir > 0 ? 'up' : 'down');
        clearTimeout(flashT.current);
        flashT.current = setTimeout(() => setFlash(null), 260);
    };

    const confirm = () => {
        if (target < 1 || bid.isPending || target === confirmed) return;
        bid.mutate({ offeringId: focal.id, percent: target }, {
            onSuccess: () => { setConfirmed(target); fx.confirm(); toast.success(`¡Tomaste ${target}%!`); },
            onError: (err) => {
                const msg = err?.status === 422 ? 'Sin cupo suficiente para ese %'
                    : err?.status === 409 ? 'La puja no está abierta'
                    : (err?.message || 'No se pudo confirmar la puja');
                toast.error(msg);
            },
        });
    };

    if (cap <= 0) {
        return (
            <div className="evx__bid evx__bid--done">
                <span className="evx__bid-cow">{focal?.name || 'Esta vaca'}</span>
                <p className="evx__bid-soldout">¡Colocada al 100%! Esperá la próxima vaca.</p>
                {confirmed > 0 && <span className="evx__bid-confirmed">Tu puja: {confirmed}%</span>}
            </div>
        );
    }

    const money = price != null ? target * price : null;

    return (
        <div className={`evx__bid ${flash ? `evx__bid--${flash}` : ''}`}>
            <header className="evx__bid-head">
                <span className="evx__bid-eyebrow">Pujando por</span>
                <span className="evx__bid-cow">{focal?.name || 'la vaca'}</span>
                <span className="evx__bid-sold">
                    {fmtPct(focal?.sold ?? 0)} pujado · <strong>{fmtPct(cap)} libre</strong>
                </span>
            </header>

            <div className="evx__bid-figure">
                <span key={target} className="evx__bid-pct">{target}<small>%</small></span>
                <span className="evx__bid-money">{money != null ? fmtMoney(money) : '—'}</span>
                <span className="evx__bid-msg">{messageFor(target)}</span>
            </div>

            <div className="evx__bid-taps">
                <button type="button" className="evx__tap evx__tap--minus" onClick={() => bump(-1)} aria-label="Restar 1%">
                    <span className="evx__tap-sign">−</span>
                    <span className="evx__tap-hint">restar</span>
                </button>
                <button type="button" className="evx__tap evx__tap--plus" onClick={() => bump(1)} aria-label="Sumar 1%">
                    <span className="evx__tap-sign">+</span>
                    <span className="evx__tap-hint">sumar</span>
                </button>
            </div>

            <button
                type="button"
                className="evx__cta evx__cta--big"
                onClick={confirm}
                disabled={target < 1 || bid.isPending || target === confirmed}
            >
                {bid.isPending ? 'Confirmando…'
                    : target === confirmed ? `Confirmado: ${confirmed}%`
                    : `Confirmar mi ${target}%`}
            </button>
            {confirmed > 0 && (
                <span className="evx__bid-confirmed">
                    Tu puja confirmada: <strong>{confirmed}%</strong>
                    {price != null && ` · ${fmtMoney(confirmed * price)}`}
                </span>
            )}
        </div>
    );
}
